/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { Switch, MenuItem, Select, CircularProgress } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { GridRowModel, GridRenderCellParams } from '@mui/x-data-grid'
import { BoStack } from '@components/systems/backoffice/BoStack'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'
import { GridColumnType, ExtendedGridColDef } from '@grid/types'
import { CardModel, formattedValue, rawValue } from './gridCard'
import dayjs from 'dayjs'

interface PropsType {
	open: boolean
	onClose: () => void
	columns: GridColumnType
	model: CardModel
	/** 편집 시작값. 새 행이면 initRow */
	row: GridRowModel | null
	isNew: boolean
	apiRef: any
	renderEditCell?: { [key: string]: (params: GridRenderCellParams) => JSX.Element }
	/** 수정 모드 전용 셀(비밀번호 변경·아바타 버튼) — 폼 맨 아래 「다른 동작」 */
	renderEditModeCell?: { [key: string]: (params: any) => JSX.Element }
	onSave: (row: GridRowModel) => void
	title: string
}

/**
 * 한 depth 들어간 편집 폼 (RESPONSIVE_PLAN §5.4, 2026-09-27 사용자 결정).
 * `editable: true` 열 = 입력, 나머지 = 읽기 스펙 행. 순서는 컬럼 순서, 섹션은 카드 힌트.
 * 검증은 blur 때 `preProcessEditCellProps`, 저장은 `onAction` 한 번.
 */
export const GridEditSheet = (props: PropsType) => {
	const { columns, model, apiRef } = props
	const [draft, setDraft] = React.useState<GridRowModel>({})
	const [errors, setErrors] = React.useState<Record<string, string>>({})
	const [saving, setSaving] = React.useState(false)

	React.useEffect(() => {
		if (!props.open) return
		setDraft({ ...(props.row ?? columns.initRow) })
		setErrors({})
		setSaving(false)
	}, [props.open, props.row, columns.initRow])

	const setField = React.useCallback((field: string, value: any) => {
		setDraft(prev => ({ ...prev, [field]: value }))
	}, [])

	const validate = React.useCallback(
		(col: ExtendedGridColDef, value: any): string => {
			if (typeof col.preProcessEditCellProps !== 'function') return ''
			try {
				const res: any = col.preProcessEditCellProps({
					id: draft.id,
					row: draft,
					props: { value },
					hasChanged: true,
					otherFieldsProps: {},
				} as any)
				if (res && typeof res.then === 'function') return ''
				if (res?.error) return String(res.errorMessage ?? '값을 확인하세요')
				return ''
			} catch {
				return ''
			}
		},
		[draft],
	)

	const onBlurField = (col: ExtendedGridColDef) => {
		const msg = validate(col, draft[col.field])
		setErrors(prev => ({ ...prev, [col.field]: msg }))
	}

	const editableCols = columns.columns.filter(
		c =>
			(c.editable === true || c.alwaysEditable === true) && !model.hide.includes(c.field),
	)

	const onSaveClick = () => {
		const nextErrors: Record<string, string> = {}
		editableCols.forEach(c => {
			const msg = validate(c, draft[c.field])
			if (msg) nextErrors[c.field] = msg
		})
		setErrors(nextErrors)
		if (Object.keys(nextErrors).length > 0) return
		setSaving(true)
		props.onSave(draft)
	}

	// renderEditCell 을 폼 안에서 부르기 위한 shim — api.setEditCellValue 가 draft 로 온다
	const editApi = React.useMemo(() => {
		const base = apiRef?.current ?? {}
		return {
			...base,
			setEditCellValue: (p: { id: any; field: string; value: any }) => {
				setField(p.field, p.value)
				return Promise.resolve(true)
			},
		}
	}, [apiRef, setField])

	const renderInput = (col: ExtendedGridColDef) => {
		const field = col.field
		const value = draft[field]
		const custom = props.renderEditCell?.[field]
		if (typeof custom === 'function') {
			const params: any = {
				id: draft.id,
				row: draft,
				field,
				value,
				formattedValue: value,
				colDef: col,
				api: editApi,
				hasFocus: false,
				tabIndex: 0,
				cellMode: 'edit',
				isEditable: true,
				rowNode: {
					id: draft.id,
					type: 'leaf',
					depth: 0,
					parent: null,
					groupingKey: null,
				},
			}
			return <View className="boEditCustom">{custom(params)}</View>
		}

		if (col.type === 'boolean') {
			const checked = value === true || value === 1 || value === '1'
			return (
				<View className="boEditSwitchRow">
					<span className="boEditSwitchText">{checked ? '예' : '아니오'}</span>
					<Switch
						checked={checked}
						onChange={(_, next) =>
							setField(field, typeof value === 'number' ? (next ? 1 : 0) : next)
						}
					/>
				</View>
			)
		}

		if (col.type === 'singleSelect') {
			const opts: any = (col as any).valueOptions
			const list: any[] = typeof opts === 'function' ? opts({ row: draft }) : opts ?? []
			return (
				<Select
					fullWidth
					size="small"
					variant="standard"
					value={value ?? ''}
					onChange={e => setField(field, e.target.value)}
					className="boEditSelect"
				>
					{list.map((o: any) => {
						const v = typeof o === 'object' ? o.value : o
						const l = typeof o === 'object' ? o.label : o
						return (
							<MenuItem key={String(v)} value={v}>
								{String(l)}
							</MenuItem>
						)
					})}
				</Select>
			)
		}

		if (col.type === 'date' || col.type === 'dateTime') {
			const d = value ? dayjs(value) : null
			return (
				<DatePicker
					value={d && d.isValid() ? d.toDate() : null}
					format="yyyy-MM-dd"
					onChange={(next: any) =>
						setField(field, next ? dayjs(next).format('YYYY-MM-DD') : '')
					}
					slotProps={{
						textField: { variant: 'standard', size: 'small', fullWidth: true },
					}}
				/>
			)
		}

		const isNumber = col.type === 'number' || typeof value === 'number'
		return (
			<input
				className={`boInput${errors[field] ? ' error' : ''}`}
				type={isNumber ? 'text' : 'text'}
				inputMode={isNumber ? 'decimal' : 'text'}
				value={value ?? ''}
				onChange={e => {
					const raw = e.target.value
					if (isNumber) {
						const n = raw === '' ? '' : Number(raw.replace(/,/g, ''))
						setField(field, raw === '' ? '' : Number.isNaN(n) ? raw : n)
					} else {
						setField(field, raw)
					}
				}}
				onBlur={() => onBlurField(col)}
				autoComplete="off"
			/>
		)
	}

	const editModeCells = Object.keys(props.renderEditModeCell ?? {}).filter(
		f => f in model.byField,
	)

	// 닫혀 있으면 아무것도 그리지 않는다 — 페이지의 renderEditModeCell 을 빈 draft 로 부르면 터진다
	if (!props.open) return null

	const bottomBar = (
		<>
			<button
				type="button"
				className="boBtnText"
				onClick={props.onClose}
				disabled={saving}
			>
				취소
			</button>
			<button type="button" className="boBtn" onClick={onSaveClick}>
				{saving ? <CircularProgress size={16} color="inherit" /> : '저장'}
			</button>
		</>
	)

	return (
		<BoStack
			open={props.open}
			onClose={props.onClose}
			title={props.isNew ? `${props.title} 추가` : `${props.title} 수정`}
			bottomBar={bottomBar}
			className="boEditSheet"
		>
			<View css={style}>
				{model.sections.map(section => {
					const fields = section.fields.filter(f => {
						const c = model.byField[f]
						return !!c && !(props.isNew && !editableCols.includes(c))
					})
					if (fields.length === 0) return null
					return (
						<View className="boEditSection" key={section.title}>
							{model.sections.length > 1 ? (
								<span className="boEditSectionTitle">{section.title}</span>
							) : null}
							{fields.map(f => {
								const col = model.byField[f]
								const editable = editableCols.includes(col)
								return (
									<View className="boEditField" key={f}>
										<span className="boLabel">{String(col.headerName ?? f)}</span>
										{editable ? (
											renderInput(col)
										) : (
											<span className="boEditRead">
												{formattedValue(col, draft, apiRef) ||
													(rawValue(col, draft, apiRef) ?? '') ||
													'—'}
											</span>
										)}
										{errors[f] ? <span className="boError">{errors[f]}</span> : null}
									</View>
								)
							})}
						</View>
					)
				})}

				{!props.isNew && editModeCells.length > 0 ? (
					<View className="boEditSection">
						<span className="boEditSectionTitle">다른 동작</span>
						{editModeCells.map(f => {
							const col = model.byField[f]
							const fn = props.renderEditModeCell?.[f]
							if (!fn) return null
							const params: any = {
								id: draft.id,
								row: draft,
								field: f,
								value: draft[f],
								colDef: col,
								api: editApi,
							}
							return (
								<View className="boEditField" key={f}>
									<span className="boLabel">{String(col.headerName ?? f)}</span>
									<View className="boEditCustom">{fn(params)}</View>
								</View>
							)
						})}
					</View>
				) : null}
			</View>
		</BoStack>
	)
}

const style = css({
	fontFamily: boFont.ui,
	color: mono.ink,
	paddingTop: 8,

	'.boEditSection': {
		paddingBottom: 8,
		'.boEditSectionTitle': {
			display: 'block',
			padding: '20px 0 4px',
			fontSize: 11,
			letterSpacing: '.2em',
			textTransform: 'uppercase',
			color: mono.faint,
			fontWeight: 500,
		},
	},

	'.boEditField': {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
		padding: '12px 0',
		borderBottom: `1px solid ${mono.line}`,

		'.boInput': { height: 44, fontSize: 16 },
		'.boEditRead': {
			minHeight: 28,
			display: 'flex',
			alignItems: 'center',
			fontSize: 15,
			fontWeight: 300,
			color: mono.dim,
			fontVariantNumeric: 'tabular-nums',
		},
		'.boEditSwitchRow': {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			minHeight: 44,
			'.boEditSwitchText': { fontSize: 15, fontWeight: 300 },
		},
		'.boEditSelect': { fontSize: 16, minHeight: 44 },
		'.boEditCustom': { minHeight: 44, display: 'flex', alignItems: 'center' },
		'.MuiInputBase-root': { fontSize: 16, minHeight: 44 },
	},
})
